/**
 * @fileOverview The player's state. Module-level, like everything in this folder: there is one
 * walkthrough for the whole app, driven from the trigger in a view and drawn by `LewViewHelp`, and it
 * has to outlive both. See useViewHelp.ts.
 */

import { ref } from "vue"
import type { Point } from "@/services/viewHelp/pointer"
import type { Rect } from "@/services/viewHelp/target"
import { ZOOM_MS, prefersReducedMotion, sleep } from "./timing"

export const isActive = ref(false)
export const tourKey = ref<string | null>(null)
export const stepIndex = ref(0)

/** True while the player is moving between steps. The clock waits; the reader's clicks overtake. */
export const isBusy = ref(false)

/**
 * True only while the frame itself is loading — opening, or the reload fallback.
 *
 * Deliberately separate from {@link isBusy}. A spinner belongs to a page load, which is slow and has
 * nothing to show; moving between steps is an animation, which *is* the thing to show, and covering
 * it with a spinner made every step feel like waiting.
 */
export const isFrameLoading = ref(false)

/**
 * True once the current step has arrived: the camera has settled on it and the pointer has come to
 * rest. The dot and the text only appear then, and the clock only runs then.
 */
export const landed = ref(false)

/**
 * True while the end screen is up: the walkthrough has been watched to its last step. Set when the last
 * step's reading time runs out, when the reader moves on from it (the dot, the text, →), and cleared by
 * any move — a seek, a key, "Von vorn".
 */
export const endShown = ref(false)

/** The route the frame is showing. Follows the app when the pointer clicks a tab. */
export const frameRoute = ref<string | null>(null)

/** A route to load the frame onto: its name, and the params and hash a step may need. */
export interface FrameTarget {
  name: string
  params?: Record<string, string>
  query?: Record<string, string>
  hash?: string
}

/**
 * What the iframe's `src` is built from. Only a reload changes it — binding `src` to
 * {@link frameRoute} would make Vue reload the frame whenever the app navigated itself, and throw
 * away the very transition the reader just watched.
 */
export const frameLoadTarget = ref<FrameTarget | null>(null)

/**
 * How many times the frame has been loaded — the iframe's `key`. A reload onto the target it was first
 * loaded onto builds the same `src`, which Vue would leave alone while the app inside has long since
 * navigated elsewhere; a new key remounts the iframe, so every reload really loads.
 */
export const frameLoads = ref(0)

/** The frame's own window, handed over by `LewViewHelpStage` once it has loaded. */
export const frameWindow = ref<Window | null>(null)

// ── the camera ────────────────────────────────────────────────────────────────

/**
 * How long the step being moved to has taken so far in its **demonstration** — the pointer's travel,
 * a menu opening, a note typed — in the same 1× milliseconds as [elapsed], so the speed control
 * divides both alike. Reset when a move starts, held while the step is up.
 *
 * A step's time is the demonstration plus the reading, and the timeline fills over both: while the
 * pointer typed, the segment used to sit at nothing and the step looked stuck.
 */
export const worked = ref(0)

/**
 * Where the frame says it is, read off the page as each step lands: the record whose page it is and
 * the tab open on it. See `services/viewHelp/trail.ts`; the header draws it after the view's name.
 */
export const frameTrail = ref<string[]>([])

/**
 * What the camera is looking at, in the frame's own coordinates — the current step's region, or a
 * tab the pointer is about to click. Null means the whole frame.
 */
export const focusRect = ref<Rect | null>(null)

/**
 * How long the camera's current move takes. Set by each move, read by `LewViewHelpStage` for the
 * camera's animation — a seek's camera moves faster than a scene's, and both follow the speed.
 */
export const cameraMs = ref(ZOOM_MS)

/**
 * A cap on the camera's zoom for the current move, or null for the usual one. Set while the pointer
 * is working, and by a step that keeps to the whole page; cleared when an ordinary step lands.
 */
export const focusCap = ref<number | null>(null)

// ── the pointer ───────────────────────────────────────────────────────────────

/** Where the pointer is, in the frame's own coordinates. Null before its first flight. */
export const pointer = ref<Point | null>(null)

/** Bumped to play a click ripple at the pointer. */
export const pointerPress = ref(0)

/**
 * Which button the last press was made with. A right-click plays a press of its own — a mouse with its
 * right button lit beside the arrow — because a ripple alone looks the same for either button, and the
 * board's menus open only on the right one.
 */
export const pointerPressButton = ref<"left" | "right">("left")

/** Plays a press at the pointer, with `button`. */
export function pressPointer(button: "left" | "right" = "left") {
  pointerPressButton.value = button
  pointerPress.value += 1
}

/**
 * True while the pointer holds its button down across a movement — a drag, a sweep over days. The ring
 * stays on under the arrow for as long as it does, so the reader sees that the button is held, not only
 * that it was pressed.
 */
export const pointerHolding = ref(false)

/**
 * The key held down for the press the pointer is making — Strg or Umschalt — drawn as a keycap beside
 * the arrow. The matrix's Strg- and Umschalt-Auswahl is a gesture of the keyboard as much as of the
 * mouse, and a pointer that only clicks shows the half of it that is not the point.
 */
export const pointerKey = ref<"ctrl" | "shift" | null>(null)

/**
 * What the pointer carries while it drags something the page does not draw moving itself — an HTML5
 * drag, whose ghost a dispatched event never gets. A box the size of what was picked up, offset from the
 * pointer by where it was grabbed, in the frame's coordinates. Null when nothing is carried.
 */
export const pointerCarry = ref<{ dx: number; dy: number; width: number; height: number } | null>(null)

/**
 * Whether the pointer is on stage. Only while it is doing something — a press, typing, a pick from a
 * menu, the walk to a tab that changes the view — and never while a step is simply being read.
 *
 * It used to be there all the time, resting beside each dot and, for a while, drifting about the
 * region to look like a hand at rest. On a still screen that somebody is reading, that was a moving
 * thing competing with the text. A pointer that appears only to act says "this is how you do it",
 * and its absence says "just look".
 */
export const pointerVisible = ref(false)

// ── playback ──────────────────────────────────────────────────────────────────

/** Whether the player advances by itself. */
export const playing = ref(true)

/**
 * Playback speed. Module scope, so it outlives one walkthrough: somebody who sped it up wants the
 * next one sped up too.
 */
export const speed = ref<number>(1)

/** How far into the current step's reading time the clock is, in milliseconds at 1×. */
export const elapsed = ref(0)

/** True while the reader's own mouse is on the dot or the text — the clock waits for them. */
export const hold = ref(false)

/**
 * Steps found to have nothing to show, so the timeline stops offering them. Discovered on the way:
 * gating decides what *may* be shown before anything loads, but whether a region is actually on the
 * page is only known once the frame is there.
 */
export const skipped = ref<Set<number>>(new Set())

/**
 * Where the player is heading, while it is on its way — so the timeline answers a click the moment
 * it happens rather than once the frame has scrolled and settled. Null when it has arrived.
 */
export const targetIndex = ref<number | null>(null)

/** A 1× duration at the current speed. */
export const scaled = (ms: number) => ms / speed.value

/** …or none at all under reduced motion, where the camera cuts and nothing glides. */
export const motionMs = (ms: number) => (prefersReducedMotion() ? 0 : scaled(ms))

// ── moves ─────────────────────────────────────────────────────────────────────

let generation = 0

/**
 * One move of the player — a step, a seek, the opening — and everything it awaits on the way.
 *
 * **The reader always wins.** A newer move, or a close, overtakes the one under way, and every await
 * in the old one checks {@link Move.current} and bows out. An earlier version simply ignored clicks
 * while a move was running — about a second of every step, and two and a half during a change of
 * view — so a click often did nothing, got repeated, and the walkthrough felt slow to answer.
 */
/**
 * Waits `ms` of *playing* time: while the clock is held — the reader's pointer is on the dot, the text
 * or the timeline — the count stands still and whatever was waiting waits on.
 *
 * This is what makes a hold stop the **demonstration** and not only the reading. The pointer's flight,
 * the beat it rests before a press, a note being typed: each is a wait, and waited in real time they
 * carried on under a reader who had put their mouse on the timeline to look at something. The step's
 * text then held, as it always did, and everything else went on regardless — which read as the hold
 * being broken.
 *
 * Polled rather than driven by the clock, so a wait unwinds by itself when the walkthrough closes:
 * `close` clears the hold, and every waiter finishes its slice and finds its move overtaken.
 */
export async function pace(ms: number, stillCurrent?: () => boolean): Promise<void> {
  // A wait belongs to a move. Overtaken, it ends at once rather than standing still under a hold: the
  // next move waits for this one to wind down (see `moveFrom`), and the reader's pointer is on the
  // timeline — holding the clock — at exactly the moment they click it. Asked of the move itself where
  // it is known (`Move.pause`): the generation current when the wait *began* would let a wait that
  // starts after its move was already overtaken run to its end, and freeze under the hold.
  const started = generation
  const current = stillCurrent ?? (() => generation === started)
  let left = ms
  while (left > 0 && current()) {
    const slice = Math.min(left, 60)
    await sleep(slice)
    if (!hold.value) left -= slice
  }
}

export class Move {
  private readonly id = ++generation

  /** False once a newer move, or a close, has overtaken this one. */
  get current(): boolean {
    return this.id === generation
  }

  /** Waits — a hold stands it still — then says whether this move is still the current one. */
  async pause(ms: number): Promise<boolean> {
    await pace(ms, () => this.current)
    return this.current
  }
}

/** Overtakes whatever move is under way without starting another — for a close. */
export function cancelMoves() {
  generation += 1
}
